import SignupCguCheckbox, { recordSignupCgu } from "@/components/price/SignupCguCheckbox";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Loader2, Users } from "lucide-react";
import PhotoPickerField from "@/components/price/PhotoPickerField";
import CityPickerInput from "@/components/price/CityPickerInput";
import { savePendingPhoto } from "@/lib/pendingPhoto";

/**
 * Price — Passenger (covoiturage) registration.
 *
 * Ultra-short cartoon/street form: first_name, last_name, email, password,
 * password_confirm. The full_name expected by the backend is composed
 * client-side by joining first + last name. Phone is sent empty because
 * the backend column is `str` (no min length) — the user can complete
 * their profile later from the dashboard.
 */
export default function ClientRegister() {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    password_confirm: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  // Ville de résidence (ex. "Saint-Denis") — géocodée via Mapbox par
  // CityPickerInput. Sert à positionner l'utilisateur sur la carte
  // publique "La communauté grandit en direct".
  const [city, setCity] = useState(null); // { name, lat, lng } | null
  // Code parrainage capturé depuis ?ref=CODE dans l'URL (persistant en
  // sessionStorage pour survivre à une navigation intermédiaire depuis
  // la page marketing).
  const [referralCode, setReferralCode] = useState("");
  // Code créateur (JEANMARC, influenceurs...) — indépendant du code parrain.
  // Live-validation contre /price/creator-codes/validate.
  const [creatorCode, setCreatorCode] = useState("");
  const [creatorInfo, setCreatorInfo] = useState(null); // {code, owner_name, wallet_bonus_eur}
  const [creatorError, setCreatorError] = useState("");
  useEffect(() => {
    const code = creatorCode.trim().toUpperCase();
    if (!code) { setCreatorInfo(null); setCreatorError(""); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/price/creator-codes/validate?code=${encodeURIComponent(code)}`);
        setCreatorInfo(data); setCreatorError("");
      } catch (err) {
        setCreatorInfo(null);
        setCreatorError(err?.response?.status === 404 ? "Code inconnu ou désactivé" : "");
      }
    }, 450);
    return () => clearTimeout(t);
  }, [creatorCode]);
  useEffect(() => {
    const fromUrl = (searchParams.get("ref") || "").trim().toUpperCase();
    if (fromUrl) {
      sessionStorage.setItem("price_referral_code", fromUrl);
      setReferralCode(fromUrl);
      return;
    }
    const stored = (sessionStorage.getItem("price_referral_code") || "").trim().toUpperCase();
    if (stored) setReferralCode(stored);
  }, [searchParams]);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [cguOk, setCguOk] = useState(false);
  const [cguVersion, setCguVersion] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const first = form.first_name.trim();
    const last = form.last_name.trim();
    const email = form.email.trim().toLowerCase();
    if (!first) return toast.error("Prénom obligatoire");
    if (!last) return toast.error("Nom obligatoire");
    if (!email || !email.includes("@")) return toast.error("Email invalide");
    if (form.password.length < 8) return toast.error("Mot de passe : 8 caractères minimum");
    if (form.password !== form.password_confirm) return toast.error("Les mots de passe ne correspondent pas");
    if (!city?.lat) return toast.error("Choisis ta ville dans la liste");
    if (!cguOk) return toast.error("Accepte les Conditions Générales d'Utilisation pour continuer");

    setSubmitting(true);
    try {
      const res = await api.post("/auth/register-client", {
        full_name: `${first} ${last}`,
        email,
        phone: "",
        password: form.password,
        preferred_language: "fr",
        ...(referralCode ? { referral_code: referralCode } : {}),
        ...(creatorCode ? { creator_code: creatorCode.trim().toUpperCase() } : {}),
        ...(city?.lat ? { city: city.name, city_lat: city.lat, city_lng: city.lng } : {}),
      });
      // Nettoyage du code stocké — évite de le réappliquer plus tard.
      try { sessionStorage.removeItem("price_referral_code"); } catch (_) { /* noop */ }
      await setSession(res.data);
      await recordSignupCgu(cguVersion);
      // Envoi silencieux de la photo optionnelle après auto-login. Si
      // ça échoue (offline / réseau), on stocke pour le prochain login.
      if (photoDataUrl) {
        try {
          await api.post("/price/me/profile-photo", { data_url: photoDataUrl });
        } catch (_) {
          savePendingPhoto(photoDataUrl);
        }
      }
      toast.success("Bienvenue sur Price !");
      // Toast bonus créateur si appliqué côté backend
      const bonus = res.data?.creator_code_bonus;
      if (bonus?.amount_eur > 0) {
        toast.success(`🎁 ${bonus.amount_eur.toFixed(0)}€ crédités sur ton portefeuille grâce à ${bonus.code}`, { duration: 5500 });
      }
      nav("/dashboard/client", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err) || "Impossible de créer le compte");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen text-foreground street-dots"
      style={{ background: "hsl(15 100% 96%)" }}
      data-testid="client-register-page"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(354, 88%, 66%, 0.35), transparent 70%)" }}
      />

      <div
        className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(24px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          paddingLeft: "max(20px, env(safe-area-inset-left))",
          paddingRight: "max(20px, env(safe-area-inset-right))",
        }}
      >
        <Link
          to="/"
          className="street-press mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-foreground street-border-thin street-shadow-sm"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Retour
        </Link>

        <header className="mt-8">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary rotate-[-4deg] street-border street-shadow-sm">
            <Users className="h-8 w-8 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <h1 className="mt-5 font-[Outfit] text-[36px] font-black leading-[1.02] tracking-tight text-foreground">
            Créer un compte
            <span className="ml-1 inline-block rounded-xl bg-accent px-2 py-0.5 street-border-thin rotate-[-2deg] text-accent-foreground">
              covoiturage
            </span>
          </h1>
          <p className="mt-3 text-sm text-foreground/70 font-medium">
            30 secondes chrono. On te demande juste l&apos;essentiel.
          </p>
        </header>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Prénom"
              value={form.first_name}
              onChange={upd("first_name")}
              placeholder="Alex"
              autoComplete="given-name"
              testid="client-register-firstname"
            />
            <Field
              label="Nom"
              value={form.last_name}
              onChange={upd("last_name")}
              placeholder="Martin"
              autoComplete="family-name"
              testid="client-register-lastname"
            />
          </div>
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={upd("email")}
            placeholder="alex@email.com"
            autoComplete="email"
            testid="client-register-email"
          />
          <Field
            label="Mot de passe"
            type="password"
            value={form.password}
            onChange={upd("password")}
            placeholder="8 caractères min."
            autoComplete="new-password"
            testid="client-register-password"
          />
          <Field
            label="Confirmer"
            type="password"
            value={form.password_confirm}
            onChange={upd("password_confirm")}
            placeholder="Retape ton mot de passe"
            autoComplete="new-password"
            testid="client-register-password-confirm"
          />

          {/* Ville — permet de placer le nouvel inscrit sur la carte
              publique sans attendre son 1er trajet. */}
          <CityPickerInput
            value={city}
            onChange={setCity}
            testid="client-register-city"
            label="Ta ville"
            required
          />

          {/* Code de parrainage — optionnel. Auto-rempli si l'utilisateur
              arrive via un lien `?ref=CODE`. Il peut aussi le saisir à
              la main s'il a reçu le code par un autre canal. */}
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-foreground/60">
              Code de parrainage (optionnel)
            </label>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 8))}
              placeholder="Ex. AB4DXG"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              data-testid="client-register-referral-input"
              className="mt-1 w-full rounded-2xl bg-white px-4 py-3 font-[Outfit] font-black tracking-widest uppercase street-border-thin street-shadow-sm outline-none focus:ring-4 focus:ring-primary/25"
              style={{ fontSize: "16px", letterSpacing: "0.15em" }}
            />
            {referralCode && (
              <div className="mt-2 text-[12px] font-bold text-primary" data-testid="client-register-referral-hint">
                Vous gagnez 1 € chacun après ton 1er trajet payé
              </div>
            )}
          </div>

          {/* Code créateur — orthogonal au code parrain. Live-validation
              contre le backend pour montrer le bonus offert. */}
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-foreground/60">
              Code créateur (optionnel)
            </label>
            <input
              type="text"
              value={creatorCode}
              onChange={(e) => setCreatorCode(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 32))}
              placeholder="Ex. JEANMARC"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              data-testid="client-register-creator-input"
              className="mt-1 w-full rounded-2xl bg-white px-4 py-3 font-[Outfit] font-black tracking-widest uppercase street-border-thin street-shadow-sm outline-none focus:ring-4 focus:ring-primary/25"
              style={{ fontSize: "16px", letterSpacing: "0.15em" }}
            />
            {creatorInfo && (
              <div className="mt-2 rounded-xl bg-emerald-50 px-3 py-2 text-[12px] font-bold text-emerald-900 street-border-thin" data-testid="client-register-creator-hint">
                🎁 <b>{creatorInfo.owner_name}</b> t'offre <b>{Number(creatorInfo.wallet_bonus_eur).toFixed(0)} €</b> sur ton portefeuille Price
              </div>
            )}
            {creatorError && (
              <div className="mt-2 text-[12px] font-bold text-red-600" data-testid="client-register-creator-error">
                ❌ {creatorError}
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm">
            <PhotoPickerField
              value={photoDataUrl}
              onChange={setPhotoDataUrl}
              testid="client-register-photo"
            />
          </div>

          <SignupCguCheckbox checked={cguOk} onChange={setCguOk} onVersion={setCguVersion} testid="client-register-cgu" />

          <button
            type="submit"
            disabled={submitting}
            className="street-press mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 font-[Outfit] text-[16px] font-black tracking-tight text-primary-foreground street-border street-shadow disabled:opacity-60"
            data-testid="client-register-submit"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Je m&apos;inscris
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-foreground/70">
          Déjà un compte ?{" "}
          <Link to="/login" className="font-bold text-primary underline-offset-2 hover:underline">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", autoComplete, testid }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-bold uppercase tracking-wide text-foreground/80">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full rounded-2xl bg-white px-4 py-3.5 font-[Outfit] text-[15px] font-medium text-foreground placeholder:text-foreground/35 street-border-thin street-shadow-sm outline-none focus:ring-4 focus:ring-primary/25"
      />
    </label>
  );
}
